import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Tabs,
  Tab
} from '@mui/material';
import Layout from '../components/Layout';
import attendanceService from '../services/attendanceService';
import employeeService from '../services/employeeService';
import '../styles/LateMarkReporting.css';

const LateMarkReportingPage = () => {
  //State management
  const [statistics, setStatistics] = useState(null);
  const [lateMarks, setLateMarks] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    month: getCurrentMonth(),
    year: new Date().getFullYear().toString(),
    employeeId: '',
    conversionStatus: 'all'
  });

  // View state
  const [activeView, setActiveView] = useState(0); // 0: overview, 1: employee-breakdown, 2: conversions, 3: all-late-marks

  // Get current month in YYYY-MM format
  function getCurrentMonth() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  // Fetch all employees for filter dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await employeeService.getAllEmployees();
        setEmployees(response.employees || []);
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Prepare filter params
      const apiFilters = {};
      if (filters.month) apiFilters.month = filters.month;
      if (filters.year && !filters.month) apiFilters.year = filters.year;
      if (filters.employeeId) apiFilters.employee_id = filters.employeeId;

      // Fetch statistics
      const statsResponse = await attendanceService.getLateMarksStatistics(apiFilters);
      setStatistics(statsResponse);

      // Fetch late marks with conversion filter
      const lateMarkFilters = { ...apiFilters };
      if (filters.conversionStatus !== 'all') {
        lateMarkFilters.is_converted = filters.conversionStatus === 'converted';
      }
      const lateMarksResponse = await attendanceService.getAllLateMarks(lateMarkFilters);
      setLateMarks(lateMarksResponse.late_marks || []);

      // Fetch conversion history
      const conversionsResponse = await attendanceService.getLateMarkConversions(apiFilters);
      setConversions(conversionsResponse.conversions || []);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load late mark data');
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString.substring(0, 5); // HH:MM
  };

  // Render statistics cards
  const renderStatisticsCards = () => {
    if (!statistics || !statistics.overall_statistics) return null;
    
    const overall = statistics.overall_statistics;
    
    return (
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-total">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" component="div" sx={{ fontSize: '2.5rem', mb: 1 }}>
                📊
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {overall.total_late_marks || 0}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, fontSize: '0.95rem', fontWeight: 500 }}>
                Total Late Marks
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                {overall.unique_employees || 0} employees
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-converted">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" component="div" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ✅
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {overall.converted_to_leave || 0}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, fontSize: '0.95rem', fontWeight: 500 }}>
                Converted to Leave
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                {overall.total_late_marks > 0 
                  ? Math.round((overall.converted_to_leave / overall.total_late_marks) * 100) 
                  : 0}% conversion rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-pending">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" component="div" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ⏳
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {overall.pending_conversion || 0}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, fontSize: '0.95rem', fontWeight: 500 }}>
                Pending Conversion
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                Not yet part of 3-strike rule
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className="stats-card stats-card-average">
            <CardContent sx={{ textAlign: 'center', color: 'white' }}>
              <Typography variant="h3" component="div" sx={{ fontSize: '2.5rem', mb: 1 }}>
                ⏱️
              </Typography>
              <Typography variant="h3" component="div" sx={{ fontWeight: 700, my: 2, fontSize: '2.5rem' }}>
                {Math.round(overall.average_minutes_per_late || 0)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, fontSize: '0.95rem', fontWeight: 500 }}>
                Avg Late Minutes
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                Total: {overall.total_late_minutes || 0} mins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render filters
  const renderFilters = () => {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Month"
                type="month"
                value={filters.month}
                onChange={(e) => handleFilterChange('month', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={filters.employeeId}
                  label="Employee"
                  onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees.map(emp => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.full_name || emp.username} - {emp.emp_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.conversionStatus}
                  label="Status"
                  onChange={(e) => handleFilterChange('conversionStatus', e.target.value)}
                >
                  <MenuItem value="all">All Late Marks</MenuItem>
                  <MenuItem value="converted">Converted Only</MenuItem>
                  <MenuItem value="pending">Pending Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <Button 
                fullWidth
                variant="outlined" 
                onClick={() => setFilters({
                  month: getCurrentMonth(),
                  year: new Date().getFullYear().toString(),
                  employeeId: '',
                  conversionStatus: 'all'
                })}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // Render employee breakdown table
  const renderEmployeeBreakdown = () => {
    if (!statistics || !statistics.employee_breakdown || statistics.employee_breakdown.length === 0) {
      return <Alert severity="info">No employee late mark data available for the selected period.</Alert>;
    }

    return (
      <Card>
        <CardHeader title="Employee-Wise Breakdown" />
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Employee Name</TableCell>
                  <TableCell>Employee Code</TableCell>
                  <TableCell align="center">Total Late Marks</TableCell>
                  <TableCell align="center">Converted</TableCell>
                  <TableCell align="center">Pending</TableCell>
                  <TableCell align="right">Total Minutes</TableCell>
                  <TableCell align="right">Avg Minutes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statistics.employee_breakdown.map((emp, index) => (
                  <TableRow key={emp.employee_id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{emp.employee_name}</TableCell>
                    <TableCell>{emp.employee_code}</TableCell>
                    <TableCell align="center">
                      <Chip label={emp.total_late_marks} color="primary" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={emp.converted} color="success" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={emp.pending} color="warning" size="small" />
                    </TableCell>
                    <TableCell align="right">{emp.total_minutes} mins</TableCell>
                    <TableCell align="right">{Math.round(emp.total_minutes / emp.total_late_marks)} mins</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  // Render monthly breakdown chart (simple bars)
  const renderMonthlyBreakdown = () => {
    if (!statistics || !statistics.monthly_breakdown || statistics.monthly_breakdown.length === 0) {
      return null;
    }

    const maxCount = Math.max(...statistics.monthly_breakdown.map(m => m.total_late_marks), 1);

    return (
      <Card sx={{ mb: 4 }}>
        <CardHeader title="Monthly Trend" />
        <CardContent>
          <div className="monthly-chart">
            {statistics.monthly_breakdown.map(month => (
              <div key={month.month} className="month-bar-container">
                <div className="month-label">{month.month}</div>
                <div className="bar-wrapper">
                  <div 
                    className="bar bar-total" 
                    style={{ height: `${(month.total_late_marks / maxCount) * 100}%` }}
                    title={`Total: ${month.total_late_marks}`}
                  >
                    <span className="bar-value">{month.total_late_marks}</span>
                  </div>
                </div>
                <div className="month-details">
                  <small>✅ {month.converted} | ⏳ {month.pending}</small>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Render conversion history
  const renderConversionHistory = () => {
    if (conversions.length === 0) {
      return <Alert severity="info">No conversion events found for the selected period.</Alert>;
    }

    return (
      <Card>
        <CardHeader title="Conversion History (3 Late Marks → 1 Leave Deduction)" />
        <CardContent>
          {conversions.map((conversion, index) => (
            <Box key={index} sx={{ mb: 4, p: 2, border: '2px solid #e9ecef', borderRadius: 2, bgcolor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  <Chip label={`Conversion #${index + 1}`} sx={{ mr: 1 }} />{' '}
                  {conversion.employee?.name} ({conversion.employee?.employee_code})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Month: {conversion.month} | Converted At: {formatDate(conversion.converted_at)}
                </Typography>
              </Box>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Late Minutes</TableCell>
                      <TableCell>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conversion.late_marks?.map((lm, lmIndex) => (
                      <TableRow key={lmIndex}>
                        <TableCell>{formatDate(lm.date)}</TableCell>
                        <TableCell>
                          <Chip label={`${lm.late_minutes} mins`} color="error" size="small" />
                        </TableCell>
                        <TableCell>{lm.notes || 'No notes'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  // Render late marks table
  const renderLateMarksTable = () => {
    if (lateMarks.length === 0) {
      return <Alert severity="info">No late marks found for the selected filters.</Alert>;
    }

    return (
      <Card>
        <CardHeader title={`All Late Marks (${lateMarks.length})`} />
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell>Shift</TableCell>
                  <TableCell>Expected Time</TableCell>
                  <TableCell>Actual Time</TableCell>
                  <TableCell align="center">Late Minutes</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lateMarks.map((lm) => (
                  <TableRow key={lm.id} hover>
                    <TableCell>{formatDate(lm.date)}</TableCell>
                    <TableCell>
                      <div>{lm.employee?.name}</div>
                      <Typography variant="caption" color="text.secondary">
                        {lm.employee?.employee_code}
                      </Typography>
                    </TableCell>
                    <TableCell>{lm.shift?.name || 'N/A'}</TableCell>
                    <TableCell>{formatTime(lm.expected_time)}</TableCell>
                    <TableCell>{formatTime(lm.actual_time)}</TableCell>
                    <TableCell align="center">
                      <Chip label={`${lm.late_minutes} mins`} color="error" size="small" />
                    </TableCell>
                    <TableCell align="center">
                      {lm.is_converted_to_leave ? (
                        <Chip label="Converted" color="success" size="small" />
                      ) : (
                        <Chip label="Pending" color="warning" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  // Main render
  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, borderBottom: 2, borderColor: 'divider', pb: 2 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            Late Mark Reporting
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor employee late arrivals, track conversion to leave deductions, and analyze trends
          </Typography>
        </Box>

        {/* Filters */}
        {renderFilters()}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
            <CircularProgress size={60} />
            <Typography sx={{ mt: 3 }}>Loading late mark data...</Typography>
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Data Display */}
        {!loading && !error && (
          <>
            {/* Statistics Cards */}
            {renderStatisticsCards()}

            {/* View Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeView} 
                onChange={(e, newValue) => setActiveView(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="📊 Overview" />
                <Tab label="👥 Employee Breakdown" />
                <Tab label="🔄 Conversions" />
                <Tab label="📋 All Late Marks" />
              </Tabs>
            </Box>

            {/* View Content */}
            {activeView === 0 && (
              <>
                {renderMonthlyBreakdown()}
                {renderEmployeeBreakdown()}
              </>
            )}

            {activeView === 1 && renderEmployeeBreakdown()}
            
            {activeView === 2 && renderConversionHistory()}
            
            {activeView === 3 && renderLateMarksTable()}
          </>
        )}
      </Container>
    </Layout>
  );
};

export default LateMarkReportingPage;
